/**
 * OAuth storage utilities using cookies for cross-subdomain support.
 *
 * SessionStorage doesn't work across subdomains, so when a user starts
 * OAuth on test.packetprospector.com but the callback goes to
 * app.packetprospector.com, the state is lost.
 *
 * Cookies with domain=.packetprospector.com work across all subdomains.
 */

const COOKIE_DOMAIN = '.packetprospector.com';
const STATE_COOKIE = 'google_auth_state';
const REDIRECT_COOKIE = 'google_auth_redirect';
const ORIGIN_COOKIE = 'google_auth_origin';
const RETURN_PATH_COOKIE = 'google_auth_return_path';
const TENANT_COOKIE = 'google_auth_tenant';

// Cookie expires in 10 minutes (plenty of time for OAuth flow)
const COOKIE_MAX_AGE = 600;

function setCookie(name: string, value: string): void {
  // In development (localhost), don't set domain
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';

  const domainPart = isLocalhost ? '' : `domain=${COOKIE_DOMAIN};`;
  const cookieString = `${name}=${encodeURIComponent(value)};${domainPart}path=/;max-age=${COOKIE_MAX_AGE};secure;samesite=lax`;

  document.cookie = cookieString;
}

function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  return null;
}

function deleteCookie(name: string): void {
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';

  // Delete cookie with domain (cross-subdomain)
  if (!isLocalhost) {
    document.cookie = `${name}=;domain=${COOKIE_DOMAIN};path=/;max-age=0;secure;samesite=lax`;
  }

  // Also delete cookie without domain (subdomain-specific) to clear any old cookies
  document.cookie = `${name}=;path=/;max-age=0;secure;samesite=lax`;

  // And try without secure flag in case old cookies were set without it
  document.cookie = `${name}=;path=/;max-age=0`;
  if (!isLocalhost) {
    document.cookie = `${name}=;domain=${COOKIE_DOMAIN};path=/;max-age=0`;
  }
}

export function setOAuthState(state: string): void {
  setCookie(STATE_COOKIE, state);
}

export function getOAuthState(): string | null {
  return getCookie(STATE_COOKIE);
}

export function clearOAuthState(): void {
  deleteCookie(STATE_COOKIE);
}

export function setOAuthRedirect(redirectUri: string): void {
  setCookie(REDIRECT_COOKIE, redirectUri);
}

export function getOAuthRedirect(): string | null {
  return getCookie(REDIRECT_COOKIE);
}

export function clearOAuthRedirect(): void {
  deleteCookie(REDIRECT_COOKIE);
}

export function setOAuthOrigin(origin: string): void {
  setCookie(ORIGIN_COOKIE, origin);
}

export function getOAuthOrigin(): string | null {
  return getCookie(ORIGIN_COOKIE);
}

export function clearOAuthOrigin(): void {
  deleteCookie(ORIGIN_COOKIE);
}

/**
 * Store the path the user should return to after OAuth login.
 * This is used to redirect field app users back to /field instead of /dashboard.
 */
export function setOAuthReturnPath(path: string): void {
  setCookie(RETURN_PATH_COOKIE, path);
}

export function getOAuthReturnPath(): string | null {
  return getCookie(RETURN_PATH_COOKIE);
}

export function clearOAuthReturnPath(): void {
  deleteCookie(RETURN_PATH_COOKIE);
}

/**
 * Store the tenant subdomain for cross-subdomain OAuth flow.
 * This ensures the correct tenant context is used when the callback
 * happens on app.packetprospector.com instead of tenant.packetprospector.com.
 */
export function setOAuthTenant(subdomain: string): void {
  setCookie(TENANT_COOKIE, subdomain);
}

export function getOAuthTenant(): string | null {
  return getCookie(TENANT_COOKIE);
}

export function clearOAuthTenant(): void {
  deleteCookie(TENANT_COOKIE);
}

export function clearAllOAuthData(): void {
  clearOAuthState();
  clearOAuthRedirect();
  clearOAuthOrigin();
  clearOAuthReturnPath();
  clearOAuthTenant();
}
